import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { users as fetchUsers } from "../services/api";

// The page no longer renders its own Sidebar/Navbar - Layout already provides
// them, so it used to draw the whole shell twice.
const Users = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchUsers()
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        setError("Could not load users. Please try again.");
        setRows([]);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((u) =>
      [u.first_name, u.last_name, u.email, u.authority]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [rows, query]);

  const initialsOf = (u) =>
    `${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`.toUpperCase() ||
    (u.email?.[0] || "?").toUpperCase();

  const roleBadge = (authority) => {
    const tone =
      authority === "SYS_ADMIN"
        ? "bg-success-50 text-success-400"
        : "bg-bgray-100 text-bgray-700";
    return (
      <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${tone}`}>
        {authority || "-"}
      </span>
    );
  };

  return (
    <div className="rounded-lg border border-bgray-200 bg-white dark:border-darkblack-400 dark:bg-darkblack-600">
      <div className="flex flex-col gap-4 border-b border-bgray-200 px-6 py-5 dark:border-darkblack-400 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-bgray-900 dark:text-white">Users</h2>
          <p className="text-sm text-bgray-600 dark:text-bgray-50">
            {rows ? `${filtered.length} of ${rows.length} shown` : "Loading users..."}
          </p>
        </div>

        <label className="relative block w-full sm:w-[280px]">
          <span className="sr-only">Search users</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or role"
            className="h-11 w-full rounded-lg border border-bgray-300 bg-white px-4 text-sm text-bgray-800 transition-colors focus:border-success-300 focus:outline-none focus:ring-2 focus:ring-green-200 dark:border-darkblack-400 dark:bg-darkblack-500 dark:text-white"
          />
        </label>
      </div>

      {error && (
        <div className="mx-6 mt-5 rounded-lg bg-error-50 px-4 py-3 text-sm font-medium text-error-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-bgray-200 dark:border-darkblack-400">
              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-bgray-500">
                Name
              </th>
              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-bgray-500">
                Email
              </th>
              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-bgray-500">
                Role
              </th>
              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-bgray-500">
                Created
              </th>
            </tr>
          </thead>
          <tbody>
            {!rows &&
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-bgray-200 dark:border-darkblack-400">
                  <td className="px-6 py-4" colSpan={4}>
                    <Skeleton height={24} />
                  </td>
                </tr>
              ))}

            {rows && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-sm text-bgray-500">
                  {rows.length === 0 ? "No users found." : "No users match that search."}
                </td>
              </tr>
            )}

            {filtered.map((u) => (
              <tr
                key={u.id}
                className="border-b border-bgray-200 last:border-0 hover:bg-bgray-100 dark:border-darkblack-400 dark:hover:bg-darkblack-500"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-300 text-xs font-bold text-white">
                      {initialsOf(u)}
                    </span>
                    <span className="text-sm font-semibold text-bgray-900 dark:text-white">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-bgray-700 dark:text-bgray-50">
                  {u.email || "-"}
                </td>
                <td className="px-6 py-4">{roleBadge(u.authority)}</td>
                <td className="px-6 py-4 text-sm text-bgray-600 dark:text-bgray-50">
                  {u.created_time ? dayjs(Number(u.created_time)).format("DD MMM YYYY") : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
